/**
 * Signup Page — design from the Claude Design auth handoff.
 *
 * Five fields (first name, last name, username, email, password) with
 * real-time inline validation, a password strength meter, a live
 * username availability check against the backend, and a terms checkbox.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authApi, billingApi } from '../services/api'
import { consumePendingCheckout } from '../utils/pendingCheckout'
import { peekPendingQuestionPath, consumePendingQuestion } from '../utils/pendingQuestion'
import { trackEvent } from '../analytics'
import { IS_NATIVE_APP } from '../services/runtime'
import AuthLayout from '../components/AuthLayout'
import {
  Field, PasswordField, Checkbox, PrimaryButton, ErrorBanner,
  FormHeader, TextLink, FootSwitch, AlertIcon, validators, MailIcon,
} from '../components/AuthFields'
import { JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'

// Debounced availability check: '' | 'checking' | 'available' | 'taken'
function useUsernameCheck(username, touched) {
  const [status, setStatus] = useState('')
  useEffect(() => {
    if (!touched || validators.username(username)) { setStatus(''); return }
    setStatus('checking')
    let cancelled = false
    const id = setTimeout(async () => {
      try {
        const res = await authApi.checkUsername(username)
        if (!cancelled) setStatus(res.data.available ? 'available' : 'taken')
      } catch {
        // If the check fails, don't block signup — the backend re-checks anyway
        if (!cancelled) setStatus('')
      }
    }, 650)
    return () => { cancelled = true; clearTimeout(id) }
  }, [username, touched])
  return status
}

export default function Signup() {
  const { signup, isGuest } = useAuth()
  const navigate = useNavigate()

  const [f, setF] = useState({ firstName: '', lastName: '', username: '', email: '', password: '' })
  const [touched, setTouched] = useState({})
  const [agree, setAgree] = useState(false)
  const [agreeErr, setAgreeErr] = useState(false)
  // Optional, and unticked by default on purpose: marketing email needs the
  // person's own yes, so it can never be pre-selected or required to sign up.
  const [marketing, setMarketing] = useState(false)
  const [formErr, setFormErr] = useState('')
  const [loading, setLoading] = useState(false)

  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setFormErr('') }
  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }))
  const err = (k) => (touched[k] ? validators[k](f[k]) : '')
  const ok = (k) => !validators[k](f[k])
  const uStatus = useUsernameCheck(f.username, touched.username)

  const allValid = ok('firstName') && ok('lastName') && ok('username') && ok('email') && ok('password')
    && uStatus !== 'taken' && uStatus !== 'checking'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setTouched({ firstName: true, lastName: true, username: true, email: true, password: true })
    if (!allValid) return
    if (!agree) { setAgreeErr(true); return }

    setFormErr('')
    setLoading(true)
    try {
      // acceptedTerms is sent so the backend can record WHEN they agreed —
      // that timestamp is what Settings → Privacy shows them later.
      await signup({ ...f, acceptedTerms: true, marketingOptIn: marketing })

      // They came here from the guest free-question wall: send them back to
      // the page they were on (Ask JAI then sends the question they had typed)
      // rather than the pricing page. Checked first because they never chose a
      // plan — the wall is what brought them, and getting straight back to
      // their question is the whole point. The pending checkout below only
      // exists if they came from Pricing, so the two don't overlap.
      const wallPath = await peekPendingQuestionPath()
      if (wallPath) {
        trackEvent('guest_limit_converted')
        // Only Ask JAI replays a saved question. Anywhere else there is
        // nothing to replay, so clear the record now rather than let it sit
        // until it expires and fire on a later, unrelated visit to /chat.
        if (!wallPath.startsWith('/chat')) await consumePendingQuestion()
        navigate(wallPath)
        return
      }

      // Resume a checkout that was interrupted to create this account (see
      // Pricing.jsx). The account exists either way; if Stripe can't be
      // reached, send them back to /pricing rather than silently dropping
      // them in the app with no idea their chosen plan never happened.
      // None of this exists on native — no Pricing page, nothing sells
      // anything (Guideline 3.1.1) — so a fresh native signup goes straight
      // to the app instead.
      if (!IS_NATIVE_APP) {
        const pending = await consumePendingCheckout()
        if (pending) {
          try {
            const { data } = await billingApi.startCheckout(pending.planId, pending.billing)
            window.location.href = data.url
            return
          } catch {
            navigate('/pricing')
            return
          }
        }

        // New account with no plan chosen yet → the pricing page, which is
        // the one moment in the journey where picking a tier is the obvious
        // next step. Starter is free and one click from there, so nobody is
        // trapped. Signing IN deliberately doesn't do this (see Login.jsx):
        // a returning user has already made this decision, and re-asking
        // every time would be a toll gate on their own app.
        navigate('/pricing')
        return
      }

      navigate('/chat')
    } catch (error) {
      const data = error.response?.data
      if (data?.errors) {
        setFormErr(Object.values(data.errors).flat().join('. '))
      } else {
        setFormErr(data?.error || 'Signup failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout screen="signup">
      <form onSubmit={handleSubmit} noValidate>
        {/* Someone arriving from a guest session isn't starting over — the
            same account is being claimed, leagues and all. Saying so removes
            the main reason a guest hesitates at this form. */}
        <FormHeader
          title="Create your account"
          sub={isGuest
            ? 'Your leagues, notes and chat history come with you — nothing is lost.'
            : 'Join JAI and get draft advice tuned to your league.'}
        />
        <ErrorBanner>{formErr}</ErrorBanner>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="First name" name="given-name" value={f.firstName} autoFocus autoComplete="given-name"
              onChange={set('firstName')} onBlur={blur('firstName')} placeholder="Jordan" error={err('firstName')} valid={ok('firstName')} />
            <Field label="Last name" name="family-name" value={f.lastName} autoComplete="family-name"
              onChange={set('lastName')} onBlur={blur('lastName')} placeholder="Rivers" error={err('lastName')} valid={ok('lastName')} />
          </div>
          <Field label="Username" name="username" value={f.username} autoComplete="username"
            onChange={(v) => set('username')(v.replace(/\s/g, ''))} onBlur={blur('username')}
            placeholder="jordanrivers" error={err('username')} valid={ok('username') && uStatus === 'available'}
            status={touched.username && !validators.username(f.username) ? uStatus : ''}
            hint="This is how leaguemates will see you." />
          <Field label="Email address" type="email" name="email" value={f.email} autoComplete="email"
            onChange={set('email')} onBlur={blur('email')} placeholder="you@example.com"
            error={err('email')} valid={ok('email')} leftIcon={<MailIcon size={16} />} />
          <PasswordField label="Password" value={f.password} name="new-password" autoComplete="new-password"
            onChange={set('password')} onBlur={blur('password')}
            placeholder="Create a password" error={err('password')} valid={ok('password')} showStrength />
        </div>
        {/* Marketing consent: UNTICKED by default and it must stay that way — a
            pre-ticked box is not valid consent in Australia. Only the copy and
            the placement are meant to change: it sits in its own panel, above
            the terms, so it is hard to miss. The guest-claim flow uses this
            same form, so it shows the same box. */}
        <div style={{ margin: '6px 0 14px', padding: '2px 0 2px 12px', borderRadius: 0, borderLeft: `3px solid ${JAI_COLORS.blue}` }}>
          <Checkbox checked={marketing} onChange={setMarketing}>
            <strong style={{ color: JAI_COLORS.navy, fontWeight: 650 }}>Send me JAI tips, new tool drops and offers.</strong> Unsubscribe anytime.
          </Checkbox>
        </div>
        <div style={{ margin: '0 0 20px' }}>
          <Checkbox checked={agree} onChange={(v) => { setAgree(v); if (v) setAgreeErr(false) }}>
            I agree to JAI's <TextLink size={12.5} onClick={() => window.open('/terms', '_blank', 'noopener')}>Terms of Service</TextLink> and{' '}
            <TextLink size={12.5} onClick={() => window.open('/privacy', '_blank', 'noopener')}>Privacy Policy</TextLink>.
          </Checkbox>
          {agreeErr && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: JAI_COLORS.error, fontSize: 11.5, fontFamily: JAI_FONTS.tight, marginTop: 8 }}>
              <AlertIcon size={13} stroke={JAI_COLORS.error} /> Please accept the terms to continue.
            </span>
          )}
        </div>
        <PrimaryButton loading={loading}>{loading ? 'Creating account…' : 'Create account'}</PrimaryButton>
        <FootSwitch prompt="Already have an account?" action="Sign in" onClick={() => navigate('/login')} />
      </form>
    </AuthLayout>
  )
}
